import { useState } from "react";
import {
  EscrowConsent, Field, FileField, GeoField, ID_TYPES, Input, NG_STATES, OnboardShell, Select, Textarea, useKycSubmit,
} from "@/pages/onboarding/shared";
import { useAuth } from "@/context/AuthContext";
import { ProfessionPicker } from "@/components/ProfessionPicker";

const STEPS = ["Professional", "Identity", "Licence", "Coverage & consent"];

const BODIES = [
  { value: "MDCN", label: "MDCN (Medical & Dental Council)" },
  { value: "NMCN", label: "NMCN (Nursing & Midwifery Council)" },
  { value: "MRTBN", label: "MRTBN (Medical Rehabilitation Therapists Board)" },
  { value: "OTHER", label: "Other professional body" },
];

export default function ProviderKyc() {
  const { user } = useAuth();
  const [step, setStep] = useState(0);
  const { busy, submit } = useKycSubmit("/api/kyc/service-provider");
  const [f, setF] = useState({
    full_name: user?.full_name || "",
    phone: user?.phone || "",
    email: user?.email || "",
    address: "",
    city: "",
    state: "",
    geolocation: null,
    profession: "",
    primary_specialty: "",
    additional_specialties: [],
    bio: "",
    id_type: "",
    id_number: "",
    id_document_file_id: "",
    photograph_file_id: "",
    license_photo_file_id: "",
    license_serial_number: "",
    license_expiry_date: "",
    registration_body: "",
    registration_number: "",
    service_coverage_areas: [],
    escrow_terms_accepted: false,
  });
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));

  const toggleArea = (state) =>
    setF((p) => ({
      ...p,
      service_coverage_areas: p.service_coverage_areas.includes(state)
        ? p.service_coverage_areas.filter((s) => s !== state)
        : [...p.service_coverage_areas, state],
    }));

  const canNext = [
    f.full_name && f.phone && f.email && f.profession && f.primary_specialty,
    f.id_type && f.id_number && f.id_document_file_id && f.photograph_file_id,
    f.license_photo_file_id && f.license_serial_number && f.license_expiry_date && f.registration_body && f.registration_number,
    f.address && f.city && f.state && f.geolocation && f.service_coverage_areas.length > 0 && f.escrow_terms_accepted,
  ][step];

  return (
    <OnboardShell
      title="Healthcare provider verification"
      subtitle="Your profile only appears in the marketplace after our team verifies your licence."
      steps={STEPS}
      step={step}
      onBack={() => setStep(step - 1)}
      onNext={() => (step === STEPS.length - 1 ? submit(f) : setStep(step + 1))}
      busy={busy}
      canNext={canNext}
      submitLabel="Submit for verification"
    >
      {step === 0 && (
        <>
          <Field label="Full name"><Input value={f.full_name} onChange={(e) => set("full_name")(e.target.value)} data-testid="sp-fullname-input" /></Field>
          <Field label="Phone number"><Input value={f.phone} onChange={(e) => set("phone")(e.target.value)} data-testid="sp-phone-input" /></Field>
          <Field label="Email address"><Input type="email" value={f.email} onChange={(e) => set("email")(e.target.value)} data-testid="sp-email-input" /></Field>
          <ProfessionPicker
            profession={f.profession}
            primarySpecialty={f.primary_specialty}
            additional={f.additional_specialties}
            onChange={(patch) => setF((p) => ({ ...p, ...patch }))}
          />
          <Field label="Professional summary"><Textarea value={f.bio} onChange={(e) => set("bio")(e.target.value)} data-testid="sp-bio-input" /></Field>
        </>
      )}
      {step === 1 && (
        <>
          <Field label="Government ID type"><Select value={f.id_type} onChange={set("id_type")} options={ID_TYPES} testId="sp-idtype-select" /></Field>
          <Field label="ID number"><Input value={f.id_number} onChange={(e) => set("id_number")(e.target.value)} data-testid="sp-idnumber-input" /></Field>
          <FileField label="Identity document" value={f.id_document_file_id} onChange={set("id_document_file_id")} purpose="GOVERNMENT_ID" testId="sp-iddoc-upload" />
          <FileField label="Professional photograph" value={f.photograph_file_id} onChange={set("photograph_file_id")} purpose="PHOTOGRAPH" testId="sp-photo-upload" />
        </>
      )}
      {step === 2 && (
        <>
          <FileField label="Professional licence photograph" value={f.license_photo_file_id} onChange={set("license_photo_file_id")} purpose="PROFESSIONAL_LICENSE" testId="sp-license-upload" />
          <Field label="Licence serial number"><Input value={f.license_serial_number} onChange={(e) => set("license_serial_number")(e.target.value)} data-testid="sp-license-serial-input" /></Field>
          <Field label="Licence expiry date"><Input type="date" value={f.license_expiry_date} onChange={(e) => set("license_expiry_date")(e.target.value)} data-testid="sp-license-expiry-input" /></Field>
          <Field label="Registration body"><Select value={f.registration_body} onChange={set("registration_body")} options={BODIES} testId="sp-regbody-select" /></Field>
          <Field label="Registration number"><Input value={f.registration_number} onChange={(e) => set("registration_number")(e.target.value)} data-testid="sp-regnumber-input" /></Field>
        </>
      )}
      {step === 3 && (
        <>
          <Field label="Practice address"><Textarea value={f.address} onChange={(e) => set("address")(e.target.value)} data-testid="sp-address-input" /></Field>
          <Field label="City / town"><Input value={f.city} onChange={(e) => set("city")(e.target.value)} data-testid="sp-city-input" /></Field>
          <Field label="State"><Select value={f.state} onChange={set("state")} options={NG_STATES} testId="sp-state-select" /></Field>
          <GeoField value={f.geolocation} onChange={set("geolocation")} testId="sp-geo-btn" />
          <div>
            <p className="text-sm font-medium text-slate-700">Service coverage areas</p>
            <div className="mt-2 flex flex-wrap gap-2" data-testid="sp-coverage-group">
              {NG_STATES.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => toggleArea(s)}
                  data-testid={`sp-coverage-${s.toLowerCase()}`}
                  className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
                    f.service_coverage_areas.includes(s)
                      ? "border-emerald-600 bg-emerald-900 text-white"
                      : "border-slate-200 bg-white text-slate-600"
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
          <EscrowConsent checked={f.escrow_terms_accepted} onChange={set("escrow_terms_accepted")} testId="sp-escrow-checkbox" />
        </>
      )}
    </OnboardShell>
  );
}
